/**
 * useAdminResourceTypes Hooks (T062 - US5)
 * React Query hooks for resource types CRUD with optimistic updates
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  type UseQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import { adminResourceTypesApi } from '../services/adminResourceTypesApi';
import type {
  AdminResourceType,
  CreateResourceTypePayload,
  UpdateResourceTypePayload,
} from '../types/admin-resource.type';

export const resourceTypeKeys = {
  all: ['admin', 'resource-types'] as const,
  lists: () => [...resourceTypeKeys.all, 'list'] as const,
};

export function useResourceTypes(): UseQueryResult<AdminResourceType[], Error> {
  return useQuery({
    queryKey: resourceTypeKeys.lists(),
    queryFn: adminResourceTypesApi.listResourceTypes,
    staleTime: 300000,
  });
}

export function useCreateResourceType(): UseMutationResult<
  AdminResourceType,
  Error,
  CreateResourceTypePayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: adminResourceTypesApi.createResourceType,
    onSuccess: (newType) => {
      queryClient.setQueryData<AdminResourceType[]>(
        resourceTypeKeys.lists(),
        (old) => (old ? [...old, newType] : [newType]),
      );
    },
  });
}

export function useUpdateResourceType(): UseMutationResult<
  AdminResourceType,
  Error,
  UpdateResourceTypePayload
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, ...payload }) =>
      adminResourceTypesApi.updateResourceType(id, payload),
    onMutate: async (updated) => {
      await queryClient.cancelQueries({ queryKey: resourceTypeKeys.lists() });
      const previous = queryClient.getQueryData<AdminResourceType[]>(
        resourceTypeKeys.lists(),
      );
      queryClient.setQueryData<AdminResourceType[]>(
        resourceTypeKeys.lists(),
        (old) =>
          old?.map((t) => (t.id === updated.id ? { ...t, ...updated } : t)),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(resourceTypeKeys.lists(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: resourceTypeKeys.lists() });
    },
  });
}

export function useDeleteResourceType(): UseMutationResult<
  AdminResourceType,
  Error,
  { id: string }
> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }) => adminResourceTypesApi.deactivateResourceType(id),
    onMutate: async ({ id }) => {
      await queryClient.cancelQueries({ queryKey: resourceTypeKeys.lists() });
      const previous = queryClient.getQueryData<AdminResourceType[]>(
        resourceTypeKeys.lists(),
      );
      queryClient.setQueryData<AdminResourceType[]>(
        resourceTypeKeys.lists(),
        (old) =>
          old?.map((t) =>
            t.id === id ? { ...t, isActive: !t.isActive } : t,
          ),
      );
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(resourceTypeKeys.lists(), context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: resourceTypeKeys.lists() });
    },
  });
}
